import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * BotPortrait — "PL" section between "¿Por qué Pre-Legal?" and "Planes".
 *
 * Layout inspired by the Camila section in analizandocantantes.com:
 *   - Giant letters "PL" at top (color of the bot's eyes — cyan)
 *   - Subtitle "Abogado Virtual de Pre-Legal"
 *   - Taglines + bullets on the LEFT column
 *   - Bot portrait on the RIGHT column
 *   - Pre-Legal brand badge top-right corner
 *   - "En línea" status pill above the bot
 */
export default function BotPortrait({ chatPath = "/chat" }) {
  const bullets = [
    "Consultas de derecho civil, penal y laboral",
    "Responde con fallos reales del Poder Judicial",
    "Disponible 24/7 — respuesta en segundos",
    "Redacción de escritos y orientación práctica",
  ];

  return (
    <section
      id="bot"
      data-testid="bot-portrait-section"
      className="relative overflow-hidden py-20 sm:py-24 px-5 sm:px-8"
      style={{
        background:
          "radial-gradient(1000px 700px at 30% 30%, rgba(0, 200, 255, 0.10), transparent 70%), linear-gradient(180deg, #05070a 0%, #0a0c11 100%)",
      }}
    >
      {/* Top-right brand badge */}
      <div
        aria-hidden="true"
        className="absolute top-6 right-6 sm:top-8 sm:right-10 text-xs font-bold tracking-[0.3em]"
        style={{ color: "var(--pl-primary)", opacity: 0.75 }}
      >
        PRELEGAL.CL
      </div>

      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* LEFT — headline + bullets + CTA */}
        <div className="order-2 lg:order-1">
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.25em] mb-4"
            style={{ color: "var(--pl-text-muted)" }}
          >
            Asistente Virtual
          </p>
          <h2
            className="font-black leading-none mb-4"
            style={{
              fontSize: "clamp(88px, 18vw, 180px)",
              color: "var(--pl-primary)",
              letterSpacing: "-0.05em",
              textShadow: "0 0 80px rgba(0, 200, 255, 0.35)",
            }}
            data-testid="bot-portrait-title"
          >
            PL
          </h2>
          <p
            className="text-xl sm:text-2xl font-semibold mb-2"
            style={{ color: "var(--pl-text-strong)" }}
          >
            Abogado Virtual de Pre-Legal
          </p>
          <p
            className="text-base sm:text-lg mb-8 max-w-md"
            style={{ color: "var(--pl-text-muted)", lineHeight: 1.55 }}
          >
            PL es tu copiloto legal dentro de Pre-Legal.cl. Te orienta con la
            ley chilena de forma rápida, clara y sin letra chica.
          </p>

          <ul className="space-y-3 mb-8">
            {bullets.map((b) => (
              <li
                key={b}
                className="flex items-start gap-3 text-sm sm:text-base"
                style={{ color: "var(--pl-text)" }}
              >
                <span
                  className="mt-[6px] flex-shrink-0 w-1.5 h-1.5 rounded-full"
                  style={{
                    background: "var(--pl-primary)",
                    boxShadow: "0 0 10px rgba(0, 200, 255, 0.6)",
                  }}
                />
                {b}
              </li>
            ))}
          </ul>

          <div
            className="rounded-xl p-4 mb-8 border"
            style={{
              background: "rgba(0, 200, 255, 0.04)",
              borderColor: "var(--pl-border-accent)",
            }}
          >
            <p className="text-sm" style={{ color: "var(--pl-text)" }}>
              💡 <strong style={{ color: "var(--pl-primary)" }}>No es un bot frío.</strong>{" "}
              PL conversa contigo como un abogado real, con criterio y tono cercano.
            </p>
          </div>

          <a
            href={chatPath}
            data-testid="bot-portrait-cta"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-base transition-all"
            style={{
              background: "var(--pl-primary)",
              color: "#001018",
              boxShadow: "0 10px 30px rgba(0, 200, 255, 0.35)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 14px 38px rgba(0, 200, 255, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 200, 255, 0.35)";
            }}
          >
            Hablar con PL
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-3 text-xs" style={{ color: "var(--pl-text-subtle)" }}>
            Disponible 24/7 · respuesta inmediata
          </p>
        </div>

        {/* RIGHT — bot portrait */}
        <div className="order-1 lg:order-2 relative flex justify-center">
          {/* "En línea" pill */}
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium"
            style={{
              background: "rgba(0, 200, 255, 0.12)",
              color: "var(--pl-primary)",
              border: "1px solid var(--pl-border-accent)",
              backdropFilter: "blur(8px)",
            }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{
                background: "var(--pl-primary)",
                boxShadow: "0 0 10px var(--pl-primary)",
                animation: "pl-pulse 2s ease-in-out infinite",
              }}
            />
            En línea
          </div>

          {/* Glow behind the bot */}
          <div
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle at center, rgba(0, 200, 255, 0.18) 0%, transparent 60%)",
              filter: "blur(40px)",
            }}
          />

          <img
            src="/bot-pl-portrait.jpg"
            alt="PL — Abogado Virtual de Pre-Legal"
            data-testid="bot-portrait-image"
            className="relative w-full max-w-[420px] rounded-3xl"
            style={{
              border: "1px solid var(--pl-border-accent)",
              boxShadow:
                "0 40px 120px rgba(0, 200, 255, 0.15), 0 0 0 1px rgba(0, 200, 255, 0.1)",
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes pl-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.4); }
        }
      `}</style>
    </section>
  );
}
